import { Fragment, useState, useEffect } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon, type IconName } from '../components/Icon'
import { InfoIcon, LearnTip } from '../lib/learn-mode'
import { useTranslation } from 'react-i18next'
import { useAuth, authHeaders } from '../lib/auth'
import { RebillGenerators } from '../components/RebillGenerators'

const TABS: Array<{ id: string; icon: IconName; labelKey: string }> = [
  { id: 'profile', icon: 'user',        labelKey: 'settings.profile' },
  { id: 'storage', icon: 'folder-open', labelKey: 'settings.storage_paths' },
  { id: 'rebills', icon: 'settings',    labelKey: 'settings.rebill_generators' },
  { id: 'about',   icon: 'info',        labelKey: 'settings.about' },
]

// Carriers that dispute e-mails can go to (carrier_disputes.carrier values).
const DISPUTE_CARRIERS = ['FEDEX', 'UPS', 'DHL', 'APC', 'EPOST', 'ASENDIA', 'USPS', 'CANADAPOST', 'EVRI', 'INTELCOM', 'UNIUNI']

interface DisputeContact {
  carrier: string; to_emails: string; cc_emails: string | null; rep_name: string | null
  subject_template: string | null; body_template: string | null
  updated_by: string | null; updated_at: string
}

// IANA timezone list shown in the dropdown.
// Label format: "Region/City (offset label)"
const TIMEZONES: Array<{ value: string; label: string }> = [
  { value: 'America/New_York',    label: 'Eastern Time — New York / Toronto' },
  { value: 'America/Chicago',     label: 'Central Time — Chicago / Dallas' },
  { value: 'America/Denver',      label: 'Mountain Time — Denver / Calgary' },
  { value: 'America/Los_Angeles', label: 'Pacific Time — Los Angeles / Vancouver' },
  { value: 'America/Phoenix',     label: 'Mountain Time — Phoenix (no DST)' },
  { value: 'America/Anchorage',   label: 'Alaska Time — Anchorage' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii Time — Honolulu' },
  { value: 'America/Halifax',     label: 'Atlantic Time — Halifax' },
  { value: 'America/St_Johns',    label: 'Newfoundland Time — St. John\'s' },
  { value: 'Europe/London',       label: 'London / Dublin' },
  { value: 'Europe/Paris',        label: 'Central European — Paris / Berlin' },
  { value: 'Asia/Istanbul',       label: 'Turkey — Istanbul' },
  { value: 'UTC',                 label: 'UTC' },
]

interface SettingRow {
  key: string
  value: string | null
  description: string | null
  category: string | null
  metadata: { type?: string; fallback?: string } | null
  updated_at: string
  updated_by: string | null
}

export function SettingsView() {
  const { t } = useTranslation()
  const [tab, setTab] = useState('profile')

  return (
    <div className="space-y-5">
      <Card className="p-7">
        <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">{t('settings.title')}</div>
        <div className="mt-2 text-[28px] font-bold text-ink-900 leading-none">{t('settings.subtitle')}</div>
        <div className="mt-2 text-[13px] text-ink-500">
          {t('settings.subtitle2')}
        </div>
        <LearnTip>
          When you host this on a cloud server (EC2, Contabo, etc.), use absolute paths
          (e.g. <code className="bg-white px-1 rounded">/var/byelabel/invoices/pdf</code>)
          mounted to durable storage. On your laptop, repo-relative paths
          (e.g. <code className="bg-white px-1 rounded">exports/customer_invoices</code>) work fine.
        </LearnTip>
      </Card>

      <div className="flex gap-2">
        {TABS.map(tabItem => (
          <button
            key={tabItem.id}
            onClick={() => setTab(tabItem.id)}
            className={`px-3.5 py-2 rounded-lg text-[13px] font-medium border flex items-center gap-2 ${
              tab === tabItem.id ? 'bg-ink-900 text-white border-ink-900' : 'bg-white text-ink-700 border-ink-200 hover:bg-ink-50'
            }`}
          >
            <Icon name={tabItem.icon} className="w-4 h-4" />
            {t(tabItem.labelKey)}
          </button>
        ))}
      </div>

      {tab === 'profile' && <ProfileSettings />}
      {tab === 'storage' && <StorageSettings />}
      {tab === 'rebills' && <RebillGenerators />}
      {tab === 'about'   && <AboutPanel />}
    </div>
  )
}

// ============================================================
// Carrier dispute contacts (who receives dispute e-mails, migration 072).
// Shown on Carrier Recovery → Settings (moved from this page, 2026-10-22).
// ============================================================

// ============================================================
// Carrier dispute rules (rules 3 + 6, migration 073): filing deadline and the
// carrier's additional-handling limits. Empty = unknown, the rule is skipped.
// ============================================================

interface DisputeRule {
  carrier: string; dispute_days: number | null
  ah_longest_in: string | null; ah_second_in: string | null; ah_girth_in: string | null
  ah_weight_lb: string | null; ah_cubic_in3: string | null
  confirmed: boolean; source: string | null; notes: string | null; updated_by: string | null
}
const RULE_FIELDS: { key: keyof DisputeRule; label: string; unit: string }[] = [
  { key: 'dispute_days',  label: 'Days to dispute',     unit: 'days' },
  { key: 'ah_longest_in', label: 'Longest side over',   unit: 'in' },
  { key: 'ah_second_in',  label: 'Second side over',    unit: 'in' },
  { key: 'ah_girth_in',   label: 'Length + girth over', unit: 'in' },
  { key: 'ah_weight_lb',  label: 'Weight over',         unit: 'lb' },
  { key: 'ah_cubic_in3',  label: 'Box volume over',     unit: 'cu in' },
]
const fmtRule = (v: unknown) => (v == null || v === '' ? '—' : String(+Number(v)))

export function DisputeRulesEditor() {
  const { can } = useAuth()
  const canEdit = can('resolve_disputes')
  const qc = useQueryClient()
  const { data } = useQuery<{ rules: DisputeRule[] }>({
    queryKey: ['dispute-rules'],
    queryFn: () => fetch('/api/dispute-rules').then(r => r.json()),
  })
  const [editing, setEditing] = useState<string | null>(null)
  const byCarrier = new Map((data?.rules ?? []).map(r => [r.carrier, r]))

  return (
    <Card className="p-6 space-y-4">
      <div>
        <div className="text-[15px] font-semibold text-ink-900">Carrier dispute rules</div>
        <div className="text-[12.5px] text-ink-500 mt-1">
          <b>Days to dispute</b>: how long after the carrier invoice date a dispute can be filed. The Disputes list shows
          "file by" and warns 15 days before. <b>Handling limits</b>: a handling-fee dispute whose declared box is under every
          limit filled in gets a "strong case" badge. Leave a value empty when it is not known. Tick <b>Confirmed</b> only
          for figures from the carrier's own guide or your rep.
        </div>
      </div>
      <div className="overflow-x-auto border border-ink-200 rounded-lg">
        <table className="w-full text-[12.5px]">
          <thead className="bg-ink-50 text-ink-500 text-[10.5px] uppercase tracking-wider">
            <tr>
              <th className="text-left py-2 px-3">Carrier</th>
              {RULE_FIELDS.map(f => <th key={f.key} className="text-right py-2 px-2 whitespace-nowrap">{f.label}</th>)}
              <th className="text-left py-2 px-2">Status</th>
              <th className="py-2 px-2" />
            </tr>
          </thead>
          <tbody>
            {DISPUTE_CARRIERS.map(car => {
              const r = byCarrier.get(car)
              return (
                <Fragment key={car}>
                  <tr className="border-t border-ink-100">
                    <td className="py-2 px-3 font-semibold">{car}</td>
                    {RULE_FIELDS.map(f => (
                      <td key={f.key} className="py-2 px-2 text-right num">
                        {r ? fmtRule(r[f.key]) : '—'}
                        {r && r[f.key] != null && <span className="text-ink-400 text-[10.5px]"> {f.unit}</span>}
                      </td>
                    ))}
                    <td className="py-2 px-2">
                      {!r ? <span className="text-ink-400">not set</span>
                        : r.confirmed ? <span className="pill pill-mint text-[10.5px]">confirmed</span>
                        : <span className="pill pill-honey text-[10.5px]" title={r.notes ?? ''}>not confirmed</span>}
                    </td>
                    <td className="py-2 px-2 text-right">
                      {canEdit && (
                        <button onClick={() => setEditing(editing === car ? null : car)}
                          className="h-7 px-3 rounded-md border border-ink-200 text-[12px] hover:bg-ink-50">
                          {editing === car ? 'Close' : r ? 'Edit' : 'Add'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {editing === car && (
                    <tr className="bg-ink-50"><td colSpan={RULE_FIELDS.length + 3} className="p-3">
                      <RuleForm carrier={car} rule={r ?? null}
                        onSaved={() => {
                          setEditing(null)
                          qc.invalidateQueries({ queryKey: ['dispute-rules'] })
                          qc.invalidateQueries({ queryKey: ['disputes'] })
                        }} />
                    </td></tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function RuleForm({ carrier, rule, onSaved }: { carrier: string; rule: DisputeRule | null; onSaved: () => void }) {
  const init = (k: keyof DisputeRule) => (rule && rule[k] != null ? String(+Number(rule[k])) : '')
  const [f, setF] = useState<Record<string, string>>(Object.fromEntries(RULE_FIELDS.map(x => [x.key, init(x.key)])))
  const [confirmed, setConfirmed] = useState(rule?.confirmed ?? false)
  const [source, setSource] = useState(rule?.source ?? '')
  const [notes, setNotes] = useState(rule?.notes ?? '')
  const [err, setErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function save() {
    setSaving(true); setErr(null)
    try {
      const r = await fetch(`/api/dispute-rules/${carrier}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, confirmed, source, notes }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) { setErr(j.error ?? `HTTP ${r.status}`); return }
      onSaved()
    } finally { setSaving(false) }
  }

  const input = 'w-full h-8 px-2 border border-ink-200 rounded-md text-[12.5px] bg-white'
  return (
    <div className="grid grid-cols-3 gap-3 text-[12px]">
      {RULE_FIELDS.map(x => (
        <label key={x.key} className="space-y-1">
          <span className="text-ink-500">{x.label} ({x.unit})</span>
          <input type="number" min={0} step="any" className={input} value={f[x.key]} placeholder="unknown"
            onChange={e => setF(p => ({ ...p, [x.key]: e.target.value }))} />
        </label>
      ))}
      <label className="col-span-3 space-y-1">
        <span className="text-ink-500">Source (a link, or which rep confirmed it)</span>
        <input className={input} value={source} onChange={e => setSource(e.target.value)} />
      </label>
      <label className="col-span-3 space-y-1">
        <span className="text-ink-500">Notes</span>
        <textarea className="w-full h-16 p-2 border border-ink-200 rounded-md text-[12.5px] bg-white"
          value={notes} onChange={e => setNotes(e.target.value)} />
      </label>
      <label className="col-span-3 flex items-center gap-2">
        <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
        Confirmed from the carrier's own guide or our rep
      </label>
      <div className="col-span-3 flex items-center gap-3">
        <button onClick={save} disabled={saving}
          className="h-8 px-4 rounded-lg bg-ink-900 text-white font-semibold text-[12.5px] disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        {err && <span className="text-flame-600">{err}</span>}
      </div>
    </div>
  )
}

export function DisputeContacts() {
  const { can } = useAuth()
  const canEdit = can('resolve_disputes')
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery<{ contacts: DisputeContact[]; defaults: { subject: string; body: string } }>({
    queryKey: ['dispute-contacts'],
    queryFn: async () => {
      const r = await fetch('/api/dispute-contacts')
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`)
      return r.json()
    },
    enabled: canEdit,
  })
  const [editing, setEditing] = useState<string | null>(null)

  if (!canEdit) {
    return <Card className="p-6 text-[13px] text-ink-500">You need the "Create / edit / resolve disputes" permission to see and edit carrier dispute contacts.</Card>
  }
  const byCarrier = new Map((data?.contacts ?? []).map(c => [c.carrier, c]))

  return (
    <Card className="p-6 space-y-4">
      <div>
        <div className="text-[15px] font-semibold text-ink-900">Carrier dispute contacts</div>
        <div className="text-[12.5px] text-ink-500 mt-1">
          Where "Send to carrier" on the Disputes page e-mails dispute requests (from info@byelabel.com).
          Each carrier gets one e-mail with a table and the summarized dispute spreadsheet attached.
          Templates can use {'{rep_name}'}, {'{carrier}'}, {'{count}'} and {'{total}'}; leave them empty for the default text.
        </div>
      </div>
      {isLoading && <div className="text-[13px] text-ink-400">Loading…</div>}
      {error && <div className="text-[13px] text-flame-700">{(error as Error).message}</div>}
      <div className="divide-y divide-ink-100 border border-ink-200 rounded-lg">
        {DISPUTE_CARRIERS.map(car => {
          const c = byCarrier.get(car)
          return (
            <div key={car} className="p-3">
              <div className="flex items-center gap-3">
                <div className="w-24 font-semibold text-[13px] text-ink-900">{car}</div>
                <div className="flex-1 text-[12.5px] text-ink-600 min-w-0">
                  {c ? (
                    <>
                      <div className="truncate">To: {c.to_emails}{c.cc_emails ? ` · Cc: ${c.cc_emails}` : ''}</div>
                      <div className="text-ink-400 text-[11.5px]">
                        {c.rep_name ? `Rep: ${c.rep_name} · ` : ''}{c.subject_template || c.body_template ? 'own template' : 'default template'}
                        {c.updated_by ? ` · updated by ${c.updated_by}` : ''}
                      </div>
                    </>
                  ) : <span className="text-ink-400">not set — this carrier's disputes cannot be e-mailed</span>}
                </div>
                <button onClick={() => setEditing(editing === car ? null : car)}
                  className="h-8 px-3 rounded-lg border border-ink-200 text-[12.5px] text-ink-700 hover:bg-ink-50">
                  {editing === car ? 'Close' : c ? 'Edit' : 'Add'}
                </button>
              </div>
              {editing === car && data && (
                <ContactForm carrier={car} contact={c ?? null} defaults={data.defaults}
                  onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['dispute-contacts'] }) }} />
              )}
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function ContactForm({ carrier, contact, defaults, onSaved }: {
  carrier: string; contact: DisputeContact | null; defaults: { subject: string; body: string }; onSaved: () => void
}) {
  const [f, setF] = useState({
    to_emails: contact?.to_emails ?? '', cc_emails: contact?.cc_emails ?? '', rep_name: contact?.rep_name ?? '',
    subject_template: contact?.subject_template ?? '', body_template: contact?.body_template ?? '',
  })
  const [err, setErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF(p => ({ ...p, [k]: e.target.value }))

  async function save() {
    setSaving(true); setErr(null)
    try {
      const r = await fetch(`/api/dispute-contacts/${carrier}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) { setErr(j.error ?? `HTTP ${r.status}`); return }
      onSaved()
    } finally { setSaving(false) }
  }

  const input = 'w-full h-8 px-2 border border-ink-200 rounded-md text-[12.5px]'
  return (
    <div className="mt-3 grid grid-cols-2 gap-3 text-[12px]">
      <label className="space-y-1"><span className="text-ink-500">To (e-mails, comma separated) *</span>
        <input className={input} value={f.to_emails} onChange={set('to_emails')} /></label>
      <label className="space-y-1"><span className="text-ink-500">Cc</span>
        <input className={input} value={f.cc_emails} onChange={set('cc_emails')} /></label>
      <label className="space-y-1"><span className="text-ink-500">Rep name (greeting; empty = "{carrier} team")</span>
        <input className={input} value={f.rep_name} onChange={set('rep_name')} /></label>
      <label className="space-y-1"><span className="text-ink-500">Subject (empty = default)</span>
        <input className={input} value={f.subject_template} placeholder={defaults.subject} onChange={set('subject_template')} /></label>
      <label className="col-span-2 space-y-1"><span className="text-ink-500">Message (empty = default; the table and attachment are added automatically)</span>
        <textarea className="w-full h-44 p-2 border border-ink-200 rounded-md text-[12.5px] font-mono"
          value={f.body_template} placeholder={defaults.body} onChange={set('body_template')} /></label>
      <div className="col-span-2 flex items-center gap-3">
        <button onClick={save} disabled={saving}
          className="h-8 px-4 rounded-lg bg-ink-900 text-white font-semibold text-[12.5px] disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        {err && <span className="text-flame-700">{err}</span>}
      </div>
    </div>
  )
}

// ============================================================
// Profile (display name + timezone)
// ============================================================
function ProfileSettings() {
  const { user, token, setToken } = useAuth()

  const [displayName, setDisplayName] = useState(user?.name ?? '')
  const [timezone,    setTimezone]    = useState(user?.tz   ?? 'America/New_York')
  const [saved,       setSaved]       = useState(false)
  const [error,       setError]       = useState<string | null>(null)

  // Keep fields in sync if user context updates (e.g. page reload)
  useEffect(() => { setDisplayName(user?.name ?? '') }, [user?.name])
  useEffect(() => { setTimezone(user?.tz   ?? 'America/New_York') }, [user?.tz])

  const dirty = displayName !== (user?.name ?? '') || timezone !== (user?.tz ?? 'America/New_York')

  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/auth/profile', {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body:    JSON.stringify({ display_name: displayName, timezone }),
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error ?? 'Save failed')
      return data as { ok: boolean; token: string }
    },
    onSuccess: (data) => {
      setToken(data.token)   // refresh JWT in context — name + tz take effect immediately
      setSaved(true)
      setError(null)
      setTimeout(() => setSaved(false), 3000)
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <Card className="p-6 space-y-6">
      <div>
        <div className="text-[14px] font-semibold text-ink-800 mb-1">Profile</div>
        <div className="text-[12px] text-ink-500">Your display name and timezone preference.</div>
      </div>

      {/* Display name */}
      <div>
        <label className="block text-[12px] font-semibold text-ink-700 mb-1.5">Display name</label>
        <input
          type="text"
          value={displayName}
          onChange={e => { setDisplayName(e.target.value); setSaved(false) }}
          className="finput w-full max-w-sm"
          placeholder="Your name"
        />
      </div>

      {/* Timezone */}
      <div>
        <label className="block text-[12px] font-semibold text-ink-700 mb-1.5">
          Timezone
        </label>
        <div className="text-[11px] text-ink-500 mb-2">
          Affects how "today", date filters, and timestamps are calculated throughout the app.
        </div>
        <select
          value={timezone}
          onChange={e => { setTimezone(e.target.value); setSaved(false) }}
          className="finput w-full max-w-sm"
        >
          {TIMEZONES.map(tz => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>
        <div className="mt-1.5 text-[11px] text-ink-400">
          Current: <code className="bg-ink-50 px-1 py-0.5 rounded">{timezone}</code>
          {' · '}
          Now: <span className="tabular-nums">
            {new Intl.DateTimeFormat('en-CA', {
              timeZone: timezone,
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit',
            }).format(new Date())}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="h-9 px-5 rounded-lg bg-ink-900 hover:bg-ink-800 text-white font-semibold text-[12px] disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : saved ? 'Saved ✓' : 'Save changes'}
        </button>
        {error && <span className="text-[12px] text-flame-600">{error}</span>}
      </div>
    </Card>
  )
}

// ============================================================
// Storage paths editor
// ============================================================
function StorageSettings() {
  const { t } = useTranslation()
  // changing a path is owner/admin only (enforced by the API too)
  const { isAdmin } = useAuth()
  const qc = useQueryClient()
  const { data, isLoading, isError } = useQuery<{ settings: SettingRow[] }>({
    queryKey: ['settings', 'storage'],
    queryFn: () => fetch('/api/settings?category=storage').then(r => r.json()),
  })

  const save = useMutation({
    mutationFn: async (args: { key: string; value: string }) => {
      const r = await fetch(`/api/settings/${encodeURIComponent(args.key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: args.value || null }),
      })
      if (!r.ok) throw new Error((await r.json())?.error || 'Save failed')
      return r.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  })

  return (
    <Card className="p-6">
      <div className="text-[14px] font-semibold text-ink-800 mb-1">
        {t('settings.storage_paths')}
        <InfoIcon tip={t('settings.storage_paths_tip')} />
      </div>
      <div className="text-[12px] text-ink-500 mb-4">
        {t('settings.storage_paths_desc')}
      </div>

      {isLoading && <div className="text-ink-400">{t('settings.loading_settings')}</div>}
      {isError && <div className="text-flame-600">{t('settings.failed_load')}</div>}

      {data?.settings.map(s => (
        <PathRow
          key={s.key}
          row={s}
          onSave={(val) => save.mutate({ key: s.key, value: val })}
          isSaving={save.isPending}
          canEdit={isAdmin}
        />
      ))}

      {data && data.settings.length === 0 && (
        <div className="text-[13px] text-ink-500 py-4">
          No storage-category settings yet. The migration{' '}
          <code className="text-[12px] bg-ink-50 px-1.5 py-0.5 rounded">sql/migrations/023_create_app_settings.sql</code>
          {' '}should seed them.
        </div>
      )}
    </Card>
  )
}

function PathRow({ row, onSave, isSaving, canEdit }: { row: SettingRow; onSave: (val: string) => void; isSaving: boolean; canEdit: boolean }) {
  const { t } = useTranslation()
  const [value, setValue] = useState(row.value ?? '')
  // Keep state in sync if the server value changes after a save
  useEffect(() => { setValue(row.value ?? '') }, [row.value])

  const dirty = (row.value ?? '') !== value
  const fallback = row.metadata?.fallback || '(built-in default)'

  return (
    <div className="border border-ink-200 rounded-lg p-4 mb-3 hover:bg-ink-50/40">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-ink-800">
            <code className="text-[12px] bg-ink-50 px-1.5 py-0.5 rounded">{row.key}</code>
          </div>
          <div className="text-[12px] text-ink-500 mt-1 leading-relaxed">{row.description}</div>
        </div>
        <div className="text-[11px] text-ink-500 whitespace-nowrap">
          updated {row.updated_at?.slice(0, 10) ?? '—'}
        </div>
      </div>

      <div className="flex gap-2 mt-3">
        <input
          type="text"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={`(default: ${fallback})`}
          className="finput flex-1 mono"
          disabled={!canEdit}
        />
        <button
          onClick={() => onSave(value)}
          disabled={!canEdit || !dirty || isSaving}
          title={canEdit ? undefined : 'Only an owner or admin can change this'}
          className="h-9 px-4 rounded-lg bg-ink-900 hover:bg-ink-800 text-white font-semibold text-[12px] disabled:opacity-40"
        >
          {isSaving ? t('common.saving') : dirty ? t('common.save') : t('common.saved')}
        </button>
        {canEdit && value !== '' && (
          <button
            onClick={() => { setValue(''); onSave('') }}
            className="h-9 px-3 rounded-lg border border-ink-200 text-ink-600 font-medium text-[12px] hover:bg-white"
            title={t('settings.reset_tip')}
          >
            {t('settings.reset')}
          </button>
        )}
      </div>

      <div className="text-[11px] text-ink-400 mt-2">
        <Icon name="info" className="w-3 h-3 inline mr-1 align-middle" />
        Default if blank: <code className="bg-ink-50 px-1 py-0.5 rounded">{fallback}</code>
      </div>
    </div>
  )
}

// ============================================================
function AboutPanel() {
  const { t } = useTranslation()
  return (
    <Card className="p-6">
      <div className="text-[14px] font-semibold text-ink-800 mb-3">{t('settings.about_title')}</div>
      <table className="w-full text-[13px]">
        <tbody>
          <tr className="border-b border-ink-100">
            <td className="py-2 pr-3 text-ink-500 w-1/3">{t('settings.about_application')}</td>
            <td className="py-2 font-semibold">ByeLabel FinOps · CFO Console</td>
          </tr>
          <tr className="border-b border-ink-100">
            <td className="py-2 pr-3 text-ink-500">{t('settings.about_version')}</td>
            <td className="py-2 mono">v0.5</td>
          </tr>
          <tr className="border-b border-ink-100">
            <td className="py-2 pr-3 text-ink-500">{t('settings.about_database')}</td>
            <td className="py-2 mono">PostgreSQL · invoices · public</td>
          </tr>
          <tr>
            <td className="py-2 pr-3 text-ink-500">{t('settings.about_documentation')}</td>
            <td className="py-2">
              <a href="/glossary" className="text-royal-600 hover:underline">Glossary →</a>
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  )
}
